/**
 * PursuitChatDrawer — "Ask this Pursuit" chatbot.
 *
 * Right-side Sheet that lets the user ask natural-language questions
 * grounded in THIS pursuit's RFP / amendments / Q&A documents, with
 * an optional fall-back to the consultancy's Global Knowledge Base.
 *
 * Multi-turn (session_id persisted per pursuit), rate-limited
 * 30 turns/hour, billed to the pursuit's wallet via the v3.14.4
 * resolver. Scope toggle: "Pursuit Only" | "Global Only" | "Both" (default).
 *
 * v3.18.18 — Requirement: per-pursuit chatbot accessible from every
 * pursuit screen so users don't have to navigate away.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import {
  Send,
  Sparkles,
  Bot,
  User,
  Plus,
  History,
  Loader2,
  Trash2,
  FileText,
  Library,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCOPE_LABELS = {
  pursuit: { label: 'Pursuit only', icon: FileText, hint: 'Only this pursuit\'s RFP, amendments and Q&A docs.' },
  global: { label: 'Library only', icon: Library, hint: 'Only your reusable library (PP, Key Personnel, Certs, Winning Proposals).' },
  both:    { label: 'Both', icon: Sparkles, hint: 'This pursuit\'s docs AND your reusable library (ranked together).' },
};

const SUGGESTIONS = [
  'Summarize the submission deadline and required volumes.',
  'List the evaluation factors and weights from Section M.',
  'Which Past Performances match this scope of work?',
  'What page limits apply to Volume II?',
];

export default function PursuitChatDrawer({ open, onOpenChange, pursuitId, pursuitName }) {
  const { getAuthHeaders } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [scope, setScope] = useState('both');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // When pursuitId changes, reset state
  useEffect(() => {
    setSessionId(null);
    setMessages([]);
    setInput('');
    setHistoryOpen(false);
  }, [pursuitId]);

  const fetchSessions = useCallback(async () => {
    if (!pursuitId) return;
    setSessionsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}/chat/sessions`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setSessions(body.sessions || []);
    } catch (e) {
      console.error('Pursuit chat sessions fetch failed', e);
    } finally {
      setSessionsLoading(false);
    }
  }, [getAuthHeaders, pursuitId]);

  useEffect(() => {
    if (open && historyOpen && pursuitId) {
      fetchSessions();
    }
  }, [open, historyOpen, pursuitId, fetchSessions]);

  const startNewConversation = () => {
    setSessionId(null);
    setMessages([]);
    setInput('');
    setHistoryOpen(false);
  };

  const loadSession = async (id) => {
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}/chat/sessions/${id}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const session = await res.json();
      setSessionId(session.id);
      setMessages(session.messages || []);
      setHistoryOpen(false);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load conversation');
    } finally {
      setSending(false);
    }
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API_URL}/api/pursuits/${pursuitId}/chat/sessions/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      setSessions((s) => s.filter((x) => x.id !== id));
      if (sessionId === id) startNewConversation();
    } catch (err) {
      console.error(err);
    }
  };

  const sendMessage = async (overrideInput) => {
    const question = (overrideInput ?? input).trim();
    if (!question || sending || !pursuitId) return;
    setSending(true);

    const optimistic = {
      role: 'user',
      content: question,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    setInput('');

    try {
      const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}/chat`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, session_id: sessionId, scope }),
      });
      if (res.status === 429) {
        const body = await res.json();
        toast.error(body?.detail?.message || 'Rate limit reached');
        setMessages((m) => m.slice(0, -1));
        setInput(question);
        return;
      }
      if (!res.ok) {
        const errBody = await res.text();
        throw new Error(errBody);
      }
      const body = await res.json();
      setSessionId(body.session_id);
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: body.answer,
          citations: body.citations || [],
          retrieved_count: body.retrieved_count,
          scope: body.scope,
          created_at: new Date().toISOString(),
        },
      ]);
    } catch (e) {
      console.error('Pursuit chat send failed', e);
      toast.error('Could not send your question. Try again.');
      setMessages((m) => m.slice(0, -1));
      setInput(question);
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl p-0 flex flex-col"
        data-testid="pursuit-chat-drawer"
      >
        <SheetHeader className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-blue-50">
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="flex items-center gap-2 text-cyan-900">
              <Sparkles className="w-5 h-5" />
              Ask this Pursuit
            </SheetTitle>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setHistoryOpen((v) => !v)}
                data-testid="pursuit-chat-history-toggle-btn"
              >
                <History className="w-4 h-4 mr-1" />
                History
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={startNewConversation}
                data-testid="pursuit-chat-new-conversation-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                New
              </Button>
            </div>
          </div>
          {pursuitName && (
            <p className="text-xs text-slate-700 font-medium truncate" title={pursuitName}>
              {pursuitName}
            </p>
          )}
          <p className="text-xs text-slate-600">
            Q&A grounded in this pursuit&apos;s documents and (optionally) your reusable library. No hallucination — every fact is cited.
          </p>
          <SheetDescription className="sr-only">
            Conversational Q&A for the active pursuit. Use the scope toggle to include or exclude your global knowledge base.
          </SheetDescription>
        </SheetHeader>

        {/* Scope toggle */}
        <div className="px-5 py-3 border-b border-slate-200 bg-white flex items-center gap-2 flex-wrap" data-testid="pursuit-chat-scope-row">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Scope:</span>
          {Object.entries(SCOPE_LABELS).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const active = scope === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setScope(key)}
                disabled={sending}
                title={cfg.hint}
                data-testid={`pursuit-chat-scope-${key}`}
                className={
                  'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition ' +
                  (active
                    ? 'bg-cyan-700 text-white border-cyan-700'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-cyan-400 hover:text-cyan-700')
                }
              >
                <Icon className="w-3 h-3" />
                {cfg.label}
              </button>
            );
          })}
        </div>

        {/* History panel */}
        {historyOpen && (
          <div
            className="border-b border-slate-200 bg-slate-50 max-h-60 overflow-y-auto"
            data-testid="pursuit-chat-history-panel"
          >
            <div className="px-4 py-2 text-xs font-semibold text-slate-600">
              Recent conversations on this pursuit
            </div>
            {sessionsLoading ? (
              <div className="px-4 py-3 space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="px-4 py-3 text-xs text-slate-500">No previous conversations.</div>
            ) : (
              <ul>
                {sessions.map((s) => (
                  <li
                    key={s.id}
                    onClick={() => loadSession(s.id)}
                    className="px-4 py-2 cursor-pointer hover:bg-white border-t border-slate-200 flex items-center justify-between"
                    data-testid={`pursuit-chat-session-row-${s.id}`}
                  >
                    <span className="text-sm text-slate-800 truncate">
                      {s.title || '(untitled)'}
                    </span>
                    <button
                      onClick={(e) => deleteSession(s.id, e)}
                      className="text-slate-400 hover:text-red-600 p-1"
                      title="Delete conversation"
                      data-testid={`pursuit-chat-session-delete-${s.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-slate-50/40" data-testid="pursuit-chat-messages">
          {messages.length === 0 ? (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">
                Ask anything about this pursuit. Try:
              </div>
              <div className="space-y-2">
                {SUGGESTIONS.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => sendMessage(s)}
                    disabled={sending}
                    className="w-full text-left text-sm bg-white hover:bg-cyan-50 border border-slate-200 hover:border-cyan-300 rounded-lg px-3 py-2 transition"
                    data-testid={`pursuit-chat-suggestion-${idx}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, idx) => (
              <div
                key={idx}
                className={'flex gap-3 ' + (m.role === 'user' ? 'justify-end' : '')}
                data-testid={`pursuit-chat-message-${m.role}-${idx}`}
              >
                {m.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-cyan-700" />
                  </div>
                )}
                <div
                  className={
                    'max-w-[78%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ' +
                    (m.role === 'user'
                      ? 'bg-cyan-700 text-white'
                      : 'bg-white border border-slate-200 text-slate-800')
                  }
                >
                  {m.content}
                  {m.role === 'assistant' && m.citations && m.citations.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {m.citations.map((c, ci) => (
                        <Badge
                          key={ci}
                          variant="outline"
                          className="text-[10px] py-0 h-5 bg-slate-50"
                          title={c.title || ''}
                        >
                          [{c.index}] {(c.title || c.asset_type || '').slice(0, 40)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                {m.role === 'user' && (
                  <div className="w-7 h-7 rounded-full bg-slate-300 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-slate-700" />
                  </div>
                )}
              </div>
            ))
          )}
          {sending && (
            <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="pursuit-chat-thinking">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Thinking…
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-slate-200 px-5 py-3 bg-white">
          <div className="flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Ask a question about this pursuit…"
              className="flex-1 min-h-[40px] max-h-32 resize-none text-sm"
              disabled={sending}
              data-testid="pursuit-chat-input"
            />
            <Button
              onClick={() => sendMessage()}
              disabled={sending || !input.trim()}
              size="sm"
              className="bg-cyan-700 hover:bg-cyan-800 self-end"
              data-testid="pursuit-chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="mt-1.5 text-[11px] text-slate-400">
            Shift+Enter for newline · Rate-limited 30 turns/hour · Cited from your documents
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
